'use client';

import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { CHANEL_BRAND_ID, DefaultValueConfig, SchemaOrgItemProp, SchemaOrgItemType } from '@headless-workspace/config';
import {
    getPriorPrice,
    MediaType,
    ProductDetailPricesContainer,
    ProductDetailPushLoyalty,
    ProductType,
    useClientData,
    useProductDetailContext,
    usePromotionInfo,
} from '@headless-workspace/domain/common/client';
import { ProductDetailValue, VipBrandsValue } from '@headless-workspace/domain/common/server';
import { GlowClickable, GlowTextBody, PropsWithFontFamily, PropsWithLocale } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { ObjectHelpers, StringHelpers } from '@headless-workspace/utils';
import { ProductDetailDeliveryZoneContainer, ProductInfoSection, ProductSeeDescription } from '../../../../client';
import { ProductDetailBanners } from '../banners';
import { ProductDetailBuyNowPayLaterText } from './ProductDetailBuyNowPayLaterText';
import { ProductRating } from './ProductRating';
import { ProductTitleWithMeta } from './ProductTitleWithMeta';
import { ProductDetailAudioContainer } from './audio';
import chanelStyles from './chanel.module.css';
import { ProductDetailSelectorContainer } from './selector';

type ProductDetailInformationProps = PropsWithLocale &
    PropsWithFontFamily & {
        product: ProductDetailValue;
        vipBrands: VipBrandsValue;
        redDotText?: string;
        onVariantHovered?: (variantId: string) => void;
    };

export const ProductDetailInformation: Component<ProductDetailInformationProps> = ({
    locale,
    product,
    vipBrands,
    fontFamily,
    redDotText,
    onVariantHovered,
}) => {
    const t = useTranslations('Discover.Pdp.information.price');

    const { getClientFlags } = useClientData();
    const isPriceLegalMentionEnabled = getClientFlags('isPriceExceptionLegalMentionsDisplayed');

    const { productDetails } = useProductDetailContext();
    const selectedProduct = productDetails ?? product;

    const promotionInformation = usePromotionInfo(selectedProduct.promotion);
    const brandTextDecoration = product.isUnderlinedBrand ? 'underline' : 'none';
    const isChanelBrand = product.brand.id === CHANEL_BRAND_ID;

    const discountPercentageLabel = selectedProduct.discountPercentage
        ? t('label.discountPercentage', {
              discountPercentage: selectedProduct.discountPercentage,
          })
        : undefined;

    const formattedTotalIndividualPrice = product.totalIndividualPrice
        ? t('label.totalIndividualPrice', {
              value: StringHelpers.formatPrice({
                  price: product.totalIndividualPrice,
                  currency: product.currency,
                  locale,
              }),
          })
        : undefined;

    const mainImage = selectedProduct.medias?.find((media) => media.type === MediaType.IMAGE);
    const productImage = mainImage ? ObjectHelpers.createImageWithSrc(mainImage.src, mainImage.alt) : undefined;

    const stickyBarImage =
        selectedProduct.type === ProductType.PRODUCT_SET
            ? productImage
            : (product.variants.find((variant) => variant.id === selectedProduct.sku)?.image ??
              ObjectHelpers.createImageWithSrc(DefaultValueConfig.imagePlaceholder));

    const priceBeforeDiscount = getPriorPrice({
        promotion: selectedProduct.promotion,
        priorPrice: selectedProduct.priorPrice ?? 0,
        originalPrice: product.originalPrice ?? 0,
    });

    const shouldDisplayAudio = product.hasAudio && product.type !== ProductType.PRODUCT_SET && product.masterId;

    return (
        <section
            id={'product-detail-information-container'}
            className={'flex flex-col flex-1 gap-3 px-1 tablet:px-1.5 desktopS:pl-0 desktop:px-0'}
        >
            <section id={'product-information'} className={'flex flex-col gap-1.5'}>
                <div id={'product-sku-detail'} className={'flex flex-col gap-0.5'}>
                    <div itemProp={SchemaOrgItemProp.Brand} itemScope itemType={SchemaOrgItemType.Brand}>
                        <GlowClickable
                            id={'product-brand'}
                            href={product.brand.url}
                            content={{
                                labelElement: (
                                    <GlowTextBody
                                        className={clsx(isChanelBrand && chanelStyles.chanelBrandText)}
                                        text={product.brand.name}
                                        textDecoration={brandTextDecoration}
                                        fontFamily={fontFamily}
                                        TagName={'span'}
                                        structuredData={{
                                            itemProp: SchemaOrgItemProp.Name,
                                        }}
                                    />
                                ),
                            }}
                            structuredData={{
                                itemProp: SchemaOrgItemProp.Url,
                                content: product.brand.url,
                            }}
                        />
                    </div>
                    <ProductTitleWithMeta name={product.name} fontFamily={fontFamily} isChanelBrand={isChanelBrand} />
                    <div className={'flex flex-col gap-1'}>
                        <div className={'flex flex-col tablet:flex-row tablet:justify-between gap-1'}>
                            <ProductSeeDescription
                                productInfoTabParams={product.productInfoTabParams}
                                fontFamily={fontFamily}
                            />
                            {product.type !== ProductType.PRODUCT_SET && (
                                <ProductRating
                                    fontFamily={fontFamily}
                                    rating={product.rating}
                                    reviewCount={product.reviewCount}
                                    productName={product.name?.full}
                                />
                            )}
                        </div>
                        <ProductDetailPricesContainer
                            price={selectedProduct.price}
                            priorPrice={selectedProduct.priorPrice}
                            originalPrice={selectedProduct.originalPrice}
                            pricePerUnit={selectedProduct.pricePerUnit}
                            currency={product.currency}
                            locale={locale}
                            fontFamily={fontFamily}
                            promotion={selectedProduct.promotion}
                            discountPercentageLabel={discountPercentageLabel}
                            priceAlwaysBlack={selectedProduct.priceAlwaysBlack}
                            enableDiscountInfo={product.enableDiscountInfo}
                            promotionInformation={promotionInformation}
                            formattedTotalIndividualPrice={formattedTotalIndividualPrice}
                            isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                        />
                        <ProductDetailBuyNowPayLaterText fontFamily={fontFamily} />
                    </div>
                </div>
                {shouldDisplayAudio && <ProductDetailAudioContainer masterProductId={product.masterId} />}

                <ProductDetailSelectorContainer
                    productName={selectedProduct.name?.full}
                    productImage={stickyBarImage}
                    brandName={product.brand.name}
                    locale={locale}
                    vipBrands={vipBrands}
                    isVipBrand={product.isVipBrand}
                    price={StringHelpers.formatPrice({
                        price: selectedProduct.price,
                        currency: selectedProduct.currency,
                        locale,
                    })}
                    priceBeforeDiscount={
                        priceBeforeDiscount
                            ? StringHelpers.formatPrice({
                                  price: priceBeforeDiscount,
                                  currency: selectedProduct.currency,
                                  locale,
                              })
                            : undefined
                    }
                    priceAlwaysBlack={selectedProduct.priceAlwaysBlack}
                    enableDiscountInfo={selectedProduct.enableDiscountInfo}
                    fontFamily={fontFamily}
                    type={product.type}
                    formats={product.formats}
                    variants={product.variants}
                    currency={product.currency}
                    squaredShade={product.squaredShade}
                    discountPercentageLabel={discountPercentageLabel}
                    isRedDot={selectedProduct.isRedDot}
                    redDotText={redDotText}
                    stock={selectedProduct.stockLevel}
                    productsSetList={product.productsSetList}
                    setHoveredVariant={onVariantHovered}
                    isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                />
            </section>
            {product.loyalty?.pointsToEarn && (
                <ProductDetailPushLoyalty
                    currency={product.currency}
                    loyaltyInformation={{
                        ...product.loyalty,
                        pointsToEarn: selectedProduct.loyaltyBenefits?.earnedPoints ?? product.loyalty?.pointsToEarn,
                    }}
                    fontFamily={fontFamily}
                    locale={locale}
                    isVipBrand={product.isVipBrand}
                />
            )}

            {product.type !== ProductType.PRODUCT_SET && <ProductDetailDeliveryZoneContainer locale={locale} />}
            <ProductInfoSection
                productInfoTabParams={product.productInfoTabParams}
                fontFamily={fontFamily}
                isEngravable={product.isEngravable}
                productImage={productImage}
            />
            <ProductDetailBanners banners={product.banners} />
        </section>
    );
};
